import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Alert, Button, Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowProgress } from '@fortawesome/pro-light-svg-icons/faArrowProgress';
import { faShuffle } from '@fortawesome/pro-regular-svg-icons/faShuffle';
import { faArrowRightToBracket } from '@fortawesome/pro-regular-svg-icons/faArrowRightToBracket';
import { useTrack } from '../../contexts/TrackContext';
import { useTrackData } from '../../contexts/TrackContext';
import { useWLC } from '../../contexts/WLCContext';
import { buildProductPath } from '../../utils';
import TrackDetailSkeleton from './TrackDetail.skeleton';
import './styles.scss';

const toTitleCase = value => {
  if (!value) return '';
  return value
    .replace(/wlc-track-/g, '')
    .replace(/_/g, '-')
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const TrackDetail = () => {
  const history = useHistory();
  const params = useParams();
  const routePreviewTrackSlug = params?.trackSlug;
  const { track, weeks, metadata, trackList } = useTrack();
  console.log('TRACK DETAIL METADATA:', metadata);
  const { wlcProgress, productSlug, authToken, updateWlcProgress } = useWLC();

  const currentTrackSlug = wlcProgress?.currentTrack;
  const currentWeekSlug = wlcProgress?.currentWeekSlug;

  // Auto-preview next track when current track is introductory
  const isIntroductoryTrack = currentTrackSlug === 'wlc-track-introductory';
  const upcomingTracksForPreview = useMemo(() => {
    const priorityOrder = wlcProgress?.trackPriorityOrder || [];
    return priorityOrder.filter(trackSlug => trackSlug !== currentTrackSlug);
  }, [wlcProgress, currentTrackSlug]);
  const nextTrackSlugForIntro = upcomingTracksForPreview[0];
  const effectivePreviewSlug = routePreviewTrackSlug || (isIntroductoryTrack ? nextTrackSlugForIntro : null);

  const isPreviewMode = !!effectivePreviewSlug;
  const shouldFetchPreviewTrack = isPreviewMode && effectivePreviewSlug !== currentTrackSlug;

  const {
    track: previewTrack,
    isLoading: isPreviewTrackLoading,
    error: previewTrackError
  } = useTrackData(effectivePreviewSlug, productSlug, authToken, {
    depth: 2,
    enabled: shouldFetchPreviewTrack
  });

  const selectedTrack = useMemo(() => {
    if (isPreviewMode) {
      if (shouldFetchPreviewTrack) {
        return previewTrack;
      }
      return track;
    }

    return track;
  }, [isPreviewMode, shouldFetchPreviewTrack, previewTrack, track]);

  const selectedTrackMetadata = useMemo(() => {
    if (!selectedTrack) {
      return metadata;
    }

    return {
      id: selectedTrack.id,
      name: selectedTrack.name,
      title: selectedTrack.title,
      slug: selectedTrack.slug,
      description: selectedTrack.displayProps?.description || selectedTrack.data?.description,
      image: selectedTrack.displayProps?.image,
      type: selectedTrack.type,
      published: selectedTrack.published,
      free: selectedTrack.free,
      createdAt: selectedTrack.createdAt
    };
  }, [selectedTrack, metadata]);

  const selectedTrackWeeks = useMemo(() => {
    if (!selectedTrack?.Children || !selectedTrack?.childrenIndexes?.week) {
      return [];
    }

    return selectedTrack.childrenIndexes.week.map(index => selectedTrack.Children[index]);
  }, [selectedTrack]);

  const currentWeekIndex = useMemo(
    () => (isPreviewMode ? -1 : selectedTrackWeeks.findIndex(week => week.slug === currentWeekSlug)),
    [isPreviewMode, selectedTrackWeeks, currentWeekSlug]
  );

  const normalizedTrackList = useMemo(() => {
    return Array.isArray(trackList) ? trackList : [];
  }, [trackList]);

  const tracksBySlug = useMemo(() => {
    return normalizedTrackList.reduce((acc, track) => {
      if (track?.slug) {
        acc[track.slug] = track;
      }
      return acc;
    }, {});
  }, [normalizedTrackList]);

  const currentTrack = tracksBySlug[currentTrackSlug] || null;

  const currentTrackIcon = useMemo(() => {
    if (isPreviewMode) {
      return (
        selectedTrack?.displayProps?.image?.url ||
        selectedTrack?.image?.url ||
        selectedTrackMetadata?.image?.url ||
        currentTrack?.image?.url
      );
    }

    return currentTrack?.image?.url || currentTrack?.displayProps?.image?.url || metadata?.image?.url;
  }, [isPreviewMode, selectedTrack, selectedTrackMetadata, currentTrack, metadata]);

  const currentTrackTitle = useMemo(() => {
    if (isPreviewMode) {
      return (
        selectedTrack?.displayProps?.title ||
        selectedTrack?.title ||
        selectedTrack?.name ||
        selectedTrackMetadata?.title ||
        selectedTrackMetadata?.name ||
        toTitleCase(effectivePreviewSlug)
      );
    }

    return currentTrack?.title || currentTrack?.name || metadata?.title || metadata?.name || 'Your Track';
  }, [isPreviewMode, selectedTrack, selectedTrackMetadata, effectivePreviewSlug, currentTrack, metadata]);

  const trackCardEyebrow = isIntroductoryTrack
    ? 'Your Next Track'
    : isPreviewMode
      ? 'Track Preview'
      : 'Your Current Track';

  const upcomingTracks = useMemo(() => {
    const priorityOrder = wlcProgress?.trackPriorityOrder || [];
    return priorityOrder.filter(trackSlug => trackSlug !== currentTrackSlug);
  }, [wlcProgress, currentTrackSlug]);

  const [orderedUpcomingTracks, setOrderedUpcomingTracks] = useState([]);
  const [draggedTrack, setDraggedTrack] = useState(null);
  const draggedTrackRef = useRef(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchDropTarget, setTouchDropTarget] = useState(null);
  const [isPlanDirty, setIsPlanDirty] = useState(false);
  const [planUpdated, setPlanUpdated] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [switchTargetTrackSlug, setSwitchTargetTrackSlug] = useState(null);

  const setTouchDragSelectionLock = useCallback(lockSelection => {
    document.body.style.webkitUserSelect = lockSelection ? 'none' : '';
    document.body.style.userSelect = lockSelection ? 'none' : '';
    document.body.style.webkitTouchCallout = lockSelection ? 'none' : '';
  }, []);

  useEffect(() => {
    setOrderedUpcomingTracks(upcomingTracks);
    setIsPlanDirty(false);
  }, [upcomingTracks]);

  useEffect(() => {
    return () => {
      setTouchDragSelectionLock(false);
    };
  }, [setTouchDragSelectionLock]);

  const handlePreviewWeek = useCallback(
    (weekSlug, weekNumber) => {
      if (!weekSlug) return;

      const previewTrackTitle =
        selectedTrack?.displayProps?.title ||
        selectedTrack?.title ||
        selectedTrack?.name ||
        selectedTrackMetadata?.title ||
        selectedTrackMetadata?.name ||
        currentTrackTitle;

      history.push(buildProductPath(productSlug, weekSlug), {
        isTrackPreview: true,
        fromTrackPlanner: true,
        previewTrackSlug: effectivePreviewSlug || currentTrackSlug,
        previewTrackTitle,
        previewWeekNumber: weekNumber
      });
      window.scrollTo(0, 0);
    },
    [
      history,
      productSlug,
      selectedTrack,
      selectedTrackMetadata,
      currentTrackTitle,
      effectivePreviewSlug,
      currentTrackSlug
    ]
  );

  const handlePreviewTrack = useCallback(
    trackSlug => {
      if (!trackSlug) return;

      history.push(buildProductPath(productSlug, `track-detail/${trackSlug}`), {
        isTrackPreview: true,
        fromTrackPlanner: true
      });
      window.scrollTo(0, 0);
    },
    [history, productSlug]
  );

  const handleUpdatePlan = useCallback(() => {
    const reorderedUpcoming = orderedUpcomingTracks.filter(trackSlug => trackSlug !== currentTrackSlug);
    const nextTrackPriorityOrder = currentTrackSlug ? [currentTrackSlug, ...reorderedUpcoming] : reorderedUpcoming;

    updateWlcProgress({ trackPriorityOrder: nextTrackPriorityOrder });
    setIsPlanDirty(false);
    setPlanUpdated(true);
    setIsConfirmModalOpen(false);
  }, [orderedUpcomingTracks, currentTrackSlug, updateWlcProgress]);

  const handleConfirmSwitchNow = useCallback(() => {
    if (!switchTargetTrackSlug) {
      return;
    }

    const normalizedPriorityOrder = Array.isArray(wlcProgress?.trackPriorityOrder)
      ? wlcProgress.trackPriorityOrder.filter(Boolean)
      : [];

    const withoutTarget = normalizedPriorityOrder.filter(trackSlug => trackSlug !== switchTargetTrackSlug);
    const shouldMoveCurrentToBottom = currentTrackSlug && currentWeekNumber < 8;

    const nextTrackPriorityOrder = shouldMoveCurrentToBottom
      ? [switchTargetTrackSlug, ...withoutTarget.filter(trackSlug => trackSlug !== currentTrackSlug), currentTrackSlug]
      : [switchTargetTrackSlug, ...withoutTarget];

    updateWlcProgress({
      switchTrackTo: switchTargetTrackSlug,
      trackPriorityOrder: nextTrackPriorityOrder
    });

    setIsPlanDirty(false);
    setPlanUpdated(true);
    setSwitchTargetTrackSlug(null);
    setIsConfirmModalOpen(false);
  }, [switchTargetTrackSlug, wlcProgress, currentTrackSlug, currentWeekNumber, updateWlcProgress]);

  const handleOpenSwitchModal = useCallback(trackSlug => {
    if (!trackSlug) {
      return;
    }

    setSwitchTargetTrackSlug(trackSlug);
    setIsConfirmModalOpen(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false);
    setSwitchTargetTrackSlug(null);
  }, []);

  const beginReorder = useCallback(trackSlug => {
    draggedTrackRef.current = trackSlug;
    setDraggedTrack(trackSlug);
    setPlanUpdated(false);
  }, []);

  const clearReorderState = useCallback(() => {
    draggedTrackRef.current = null;
    setDraggedTrack(null);
    setTouchDropTarget(null);
  }, []);

  const reorderDraggedTrack = useCallback(targetTrackSlug => {
    const sourceTrackSlug = draggedTrackRef.current;

    if (!sourceTrackSlug || sourceTrackSlug === targetTrackSlug) {
      return;
    }

    setOrderedUpcomingTracks(prev => {
      const sourceIndex = prev.indexOf(sourceTrackSlug);
      const targetIndex = prev.indexOf(targetTrackSlug);

      if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });

    setIsPlanDirty(true);
  }, []);

  const handleDragStart = useCallback(
    trackSlug => {
      beginReorder(trackSlug);
    },
    [beginReorder]
  );

  const handleDragOver = useCallback(event => {
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback(
    targetTrackSlug => {
      reorderDraggedTrack(targetTrackSlug);
    },
    [reorderDraggedTrack]
  );

  const handleDrop = useCallback(
    event => {
      event.preventDefault();
      clearReorderState();
    },
    [clearReorderState]
  );

  const handleDragEnd = useCallback(() => {
    clearReorderState();
  }, [clearReorderState]);

  const handleTouchStart = useCallback(
    (event, trackSlug) => {
      if (event.cancelable) {
        event.preventDefault();
      }

      setTouchDragSelectionLock(true);
      setIsTouchDragging(true);
      beginReorder(trackSlug);
    },
    [beginReorder, setTouchDragSelectionLock]
  );

  const handleTouchMove = useCallback(
    event => {
      if (!draggedTrackRef.current) {
        return;
      }

      const touch = event.touches?.[0];
      if (!touch) {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);
      const targetCard = targetElement?.closest('[data-track-slug]');
      const targetTrackSlug = targetCard?.getAttribute('data-track-slug');

      if (!targetTrackSlug) {
        setTouchDropTarget(null);
        return;
      }

      setTouchDropTarget(targetTrackSlug);

      reorderDraggedTrack(targetTrackSlug);
    },
    [reorderDraggedTrack]
  );

  const handleTouchEnd = useCallback(() => {
    setIsTouchDragging(false);
    setTouchDragSelectionLock(false);
    clearReorderState();
  }, [clearReorderState, setTouchDragSelectionLock]);

  useEffect(() => {
    if (!isTouchDragging) {
      return undefined;
    }

    const handleWindowTouchMove = event => {
      handleTouchMove(event);
    };

    const handleWindowTouchEnd = () => {
      handleTouchEnd();
    };

    window.addEventListener('touchmove', handleWindowTouchMove, { passive: false });
    window.addEventListener('touchend', handleWindowTouchEnd);
    window.addEventListener('touchcancel', handleWindowTouchEnd);

    return () => {
      window.removeEventListener('touchmove', handleWindowTouchMove);
      window.removeEventListener('touchend', handleWindowTouchEnd);
      window.removeEventListener('touchcancel', handleWindowTouchEnd);
    };
  }, [isTouchDragging, handleTouchMove, handleTouchEnd]);

  const moveTrack = useCallback((trackSlug, direction) => {
    setOrderedUpcomingTracks(prev => {
      const index = prev.indexOf(trackSlug);
      if (index === -1) return prev;

      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });

    setIsPlanDirty(true);
    setPlanUpdated(false);
  }, []);

  const getWeekStatus = useCallback(
    index => {
      if (currentWeekIndex === -1) return 'upcoming';
      if (index < currentWeekIndex) return 'complete';
      if (index === currentWeekIndex) return 'current';
      return 'upcoming';
    },
    [currentWeekIndex]
  );

  const currentWeekNumber = currentWeekIndex >= 0 ? currentWeekIndex + 1 : 1;
  const nextTrackSlug = orderedUpcomingTracks?.[0];
  const confirmTrackSlug = switchTargetTrackSlug || nextTrackSlug;

  const confirmTrackTitle = useMemo(() => {
    if (!confirmTrackSlug) {
      return 'your next';
    }

    const nextTrack = tracksBySlug[confirmTrackSlug];
    return nextTrack?.title || nextTrack?.name || toTitleCase(confirmTrackSlug);
  }, [tracksBySlug, confirmTrackSlug]);

  const hasNoSelectedTrack = isPreviewMode && !isPreviewTrackLoading && !selectedTrack;
  const isTrackDetailLoading =
    isPreviewTrackLoading || (!!currentTrackSlug && (!selectedTrack || !normalizedTrackList.length));

  if (isTrackDetailLoading) {
    return <TrackDetailSkeleton />;
  }

  return (
    <div>
      <Container className="wlcTrackDetailPage pb-5">
        {previewTrackError && (
          <Row>
            <Col>
              <Alert color="danger" className="mb-4">
                We couldn&apos;t load that track preview right now.
              </Alert>
            </Col>
          </Row>
        )}
        {hasNoSelectedTrack && (
          <Row>
            <Col>
              <Alert color="warning" className="mb-4">
                We couldn&apos;t find a track for slug <strong>{effectivePreviewSlug}</strong>.
              </Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <Card className="wlcTrackCurrentCard border-0 mb-4 background-light-purple">
              <CardBody className="p-4">
                <Row className="align-items-center">
                  <Col style={{ maxWidth: '100px' }}>
                    {currentTrackIcon ? (
                      <img src={currentTrackIcon} alt={currentTrackTitle} className="wlcTrackWeekIcon" />
                    ) : (
                      <div className="wlcTrackWeekIcon wlcTrackIconFallback d-inline-block" aria-hidden="true" />
                    )}
                  </Col>
                  <Col>
                    <p className="text-muted mb-1">{trackCardEyebrow}</p>
                    <h4 className="my-0">{currentTrackTitle}</h4>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            {selectedTrackWeeks.map((week, index) => {
              const status = getWeekStatus(index);
              const isComplete = status === 'complete';
              const isCurrent = status === 'current';
              const weekTitle = week.displayProps?.title || week.title || week.name;
              const weekImage = week.displayProps?.image?.url || week.data?.thumb?.url;

              return (
                <>
                  <Card className="wlcTrackWeekCard border-0 mb-3 background-light-gray" key={week.id || week.slug}>
                    {isComplete && (
                      <CardHeader className="wlcTrackWeekBadge wlcTrackWeekBadgeComplete py-2 px-3 background-purple">
                        Completed. You did it!
                      </CardHeader>
                    )}
                    {isCurrent && (
                      <CardHeader className="wlcTrackWeekBadge wlcTrackWeekBadgeCurrent py-2 px-3 background-primary">
                        You are here!
                      </CardHeader>
                    )}
                    <CardBody className="p-4">
                      <Row className="align-items-center">
                        <Col xs="9">
                          <p className="text-muted mb-1">Week {index + 1}</p>
                          <h4 className="my-0 text-balance">{weekTitle}</h4>
                          <Button
                            type="button"
                            color="link"
                            className="wlcTrackPreviewLink p-0 text-purple"
                            onClick={() => handlePreviewWeek(week.slug, index + 1)}
                            aria-label={`Preview ${weekTitle}`}
                          >
                            Preview&nbsp;
                            <FontAwesomeIcon icon={faArrowRightToBracket} aria-hidden="true" />
                          </Button>
                        </Col>
                        <Col xs="3" className="text-right">
                          {weekImage ? (
                            <img src={weekImage} alt={weekTitle || `Week ${index + 1}`} className="wlcTrackWeekIcon" />
                          ) : (
                            <div
                              className="wlcTrackWeekIcon wlcTrackWeekIconFallback d-inline-block"
                              aria-hidden="true"
                            />
                          )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                  {isCurrent && index !== selectedTrackWeeks.length - 1 && (
                    <div className="my-5 text-purple text-center">
                      <FontAwesomeIcon icon={faArrowProgress} aria-hidden="true" size="2x" />
                    </div>
                  )}
                </>
              );
            })}
          </Col>
        </Row>
      </Container>

      <div className="background-dark-purple py-5">
        <Container className="wlcTrackDetailPage">
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>
              <h2 className="text-white mt-0 mb-2">Your Upcoming Tracks</h2>
              <p className="text-white">
                Each track is 8 weeks long. Drag to reorder what comes next after you finish your current track. Hit
                "Update My Plan" to save your changes.
              </p>
              <p className="text-white mb-3">
                <em>Want to switch sooner?</em> Tap "Switch Now."
              </p>

              {orderedUpcomingTracks.map(trackSlug =>
                (() => {
                  const upcomingTrack = tracksBySlug[trackSlug] || null;
                  const upcomingTrackTitle = upcomingTrack?.title || upcomingTrack?.name || toTitleCase(trackSlug);
                  const upcomingTrackIcon = upcomingTrack?.image?.url || upcomingTrack?.displayProps?.image?.url;

                  return (
                    <Card
                      className={`wlcUpcomingTrackCard border-0 mb-2 ${draggedTrack === trackSlug ? 'is-dragging' : ''} ${
                        isTouchDragging && touchDropTarget === trackSlug && draggedTrack !== trackSlug
                          ? 'is-touch-drop-target'
                          : ''
                      }`}
                      key={trackSlug}
                      data-track-slug={trackSlug}
                      draggable
                      onDragStart={() => handleDragStart(trackSlug)}
                      onDragOver={handleDragOver}
                      onDragEnter={() => handleDragEnter(trackSlug)}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      onTouchStart={event => handleTouchStart(event, trackSlug)}
                    >
                      <CardBody className="py-3 px-4">
                        <Row className="align-items-center">
                          <Col style={{ maxWidth: '100px' }}>
                            {upcomingTrackIcon ? (
                              <img src={upcomingTrackIcon} alt={upcomingTrackTitle} className="wlcTrackWeekIcon" />
                            ) : (
                              <div
                                className="wlcTrackWeekIcon wlcTrackIconFallback d-inline-block"
                                aria-hidden="true"
                              />
                            )}
                          </Col>
                          <Col>
                            <h4 className="mt-0 mb-1 text-balance">{upcomingTrackTitle}</h4>
                            <p className="mb-1 text-muted wlcUpcomingTrackDragHint text-small">Drag to reorder</p>
                            <Button
                              type="button"
                              color="link"
                              className="wlcTrackPreviewLink p-0 text-purple mr-3"
                              onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                handlePreviewTrack(trackSlug);
                              }}
                              aria-label={`Preview ${upcomingTrackTitle}`}
                            >
                              Preview&nbsp;
                              <FontAwesomeIcon icon={faArrowRightToBracket} aria-hidden="true" />
                            </Button>
                            &nbsp;
                            <Button
                              type="button"
                              outline
                              color="primary"
                              size="sm"
                              onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleOpenSwitchModal(trackSlug);
                              }}
                            >
                              Switch Now&nbsp;
                              <FontAwesomeIcon icon={faShuffle} />
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  );
                })()
              )}

              <Button
                type="button"
                color="purple"
                size="lg"
                className="mt-3"
                onClick={handleUpdatePlan}
                disabled={!isPlanDirty}
              >
                Update My Plan
              </Button>
              {planUpdated && (
                <Alert color="success" className="mt-3 mb-0" role="status">
                  Your track order was saved.
                </Alert>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={isConfirmModalOpen} toggle={handleCloseConfirmModal} centered>
        <ModalBody className="p-4">
          <h4 className="mt-0 mb-3">Are you sure?</h4>
          <p className="mb-3">
            When you switch tracks, you'll start <strong>week 1</strong> of the <strong>{confirmTrackTitle}</strong>{' '}
            track on Monday.
          </p>
          {currentWeekNumber < 7 && currentTrackSlug !== 'wlc-track-introductory' && (
            <p className="mb-4">
              You can always switch back to your current track, and you'll restart on{' '}
              <strong>week {currentWeekNumber + 1}</strong> of the <strong>{currentTrackTitle}</strong> track.
            </p>
          )}

          <Button type="button" color="purple" block className="mb-2" onClick={handleConfirmSwitchNow}>
            Yes, I want to switch my track.
          </Button>
          <Button type="button" color="light" block onClick={handleCloseConfirmModal}>
            No, I'll stay on my current track.
          </Button>
        </ModalBody>
      </Modal>
    </div>
  );
};

TrackDetail.displayName = 'TrackDetail';

export default TrackDetail;
